"use client";

import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { useToolNumber } from "@/components/tools/tool-ui";
import type { Scheduled } from "@/lib/tool-kit/accounting/cpm";
import { CHART_FONT_SIZE, CHART_WIDTH } from "@/lib/tool-kit/charts/layout";
import { linearScale, niceTicks } from "@/lib/tool-kit/charts/scale";
import { maxTextWidth, truncateText } from "@/lib/tool-kit/charts/text";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const ROW = 24;
const BAR = 12;
const TOP = 30;
const BOTTOM = 36;
const RIGHT = 16;
const LABEL_MAX = 150;
const CRITICAL = CHART_COLORS[3];
const NORMAL = CHART_COLORS[0];

export function CpmGantt({
  activities,
  duration,
  labels,
}: {
  activities: readonly Scheduled[];
  duration: number;
  labels: {
    id: string;
    label: string;
    description: string;
    critical: string;
    normal: string;
    slack: string;
  };
}) {
  const fmt = useToolNumber();
  const rows = [...activities].sort((a, b) => a.es - b.es);
  const names = rows.map((a) =>
    truncateText(a.name ? `${a.id} ${a.name}` : a.id, LABEL_MAX, {
      fontSize: CHART_FONT_SIZE.tick,
    })
  );
  const left =
    Math.min(
      LABEL_MAX,
      maxTextWidth(names, { fontSize: CHART_FONT_SIZE.tick })
    ) + 16;
  const ticks = niceTicks(0, duration, { includeZero: true, integer: false });
  const width = CHART_WIDTH;
  const height = TOP + rows.length * ROW + BOTTOM;
  const x = linearScale([ticks.min, ticks.max], [left, width - RIGHT]);
  const legend = [
    { text: labels.critical, color: CRITICAL, opacity: 1 },
    { text: labels.normal, color: NORMAL, opacity: 1 },
    { text: labels.slack, color: NORMAL, opacity: 0.3 },
  ];
  return (
    <ChartSvg
      description={labels.description}
      height={height}
      id={labels.id}
      label={labels.label}
      width={width}
    >
      {ticks.ticks.map((tick) => (
        <g key={tick}>
          <line
            stroke={CHART_INK.grid}
            x1={px(x(tick))}
            x2={px(x(tick))}
            y1={TOP - 6}
            y2={TOP + rows.length * ROW}
          />
          <text
            fill={CHART_INK.muted}
            fontSize={CHART_FONT_SIZE.tick}
            textAnchor="middle"
            x={px(x(tick))}
            y={TOP - 12}
          >
            {fmt(tick, { maximumFractionDigits: 2 })}
          </text>
        </g>
      ))}
      {rows.map((a, i) => {
        const y = TOP + i * ROW + (ROW - BAR) / 2;
        const color = a.critical ? CRITICAL : NORMAL;
        return (
          <g key={a.id}>
            <text
              fill={CHART_INK.text}
              fontSize={CHART_FONT_SIZE.tick}
              fontWeight={a.critical ? 700 : 400}
              textAnchor="end"
              x={left - 8}
              y={px(y + BAR - 2)}
            >
              {names[i]}
            </text>
            {a.totalSlack > 0 ? (
              <rect
                fill={NORMAL}
                fillOpacity={0.3}
                height={BAR}
                width={px(x(a.lf) - x(a.ef))}
                x={px(x(a.ef))}
                y={px(y)}
              />
            ) : null}
            <rect
              fill={color}
              height={BAR}
              stroke={a.critical ? CHART_INK.text : "none"}
              strokeWidth={a.critical ? 1 : 0}
              width={Math.max(2, px(x(a.ef) - x(a.es)))}
              x={px(x(a.es))}
              y={px(y)}
            />
          </g>
        );
      })}
      {legend.map((item, i) => (
        <g key={item.text}>
          <rect
            fill={item.color}
            fillOpacity={item.opacity}
            height={10}
            width={14}
            x={left + i * 150}
            y={height - 22}
          />
          <text
            fill={CHART_INK.text}
            fontSize={CHART_FONT_SIZE.legend}
            x={left + i * 150 + 20}
            y={height - 13}
          >
            {item.text}
          </text>
        </g>
      ))}
    </ChartSvg>
  );
}
